import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { RETURN_TO_PARAM, ROUTES, type MeResponse, type SessionUser } from '@bioterafi/api-contract';
import type { FetchLike } from '../data/api';
import { LeaveGuardContext, type LeaveGuardRegistry } from '../components/leaveGuard';
import { appFetch, withBearerToken } from './runtime';

export type { FetchLike };

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed_in'; user: SessionUser }
  | { status: 'signed_out' }
  /** The session could not be checked (network or server failure). Treated as signed out in the top bar. */
  | { status: 'error'; message: string };

export interface SessionContextValue {
  state: SessionState;
  /** The signed-in user, or null. */
  user: SessionUser | null;
  /** Reloads the session from the API. */
  refresh: () => void;
  /**
   * Asks first if the page has unsaved changes, ends the session on the server,
   * then goes to the landing page. Resolves false if the reader chose to stay
   * (nothing was ended); rejects if the server refused.
   */
  signOut: () => Promise<boolean>;
  /** The fetch used for API calls, so pages share the provider's transport (and tests can replace it). */
  apiFetch: FetchLike;
}

const SessionContext = createContext<SessionContextValue | null>(null);

const defaultFetch: FetchLike = (input, init) => appFetch(input, init);

/**
 * What the session needs from Clerk (ADR-0021). The real one wraps Clerk's useAuth() (src/auth/clerk.tsx);
 * tests pass a fake, so this file never imports @clerk/react.
 */
export interface ClerkAdapter {
  /** False until Clerk has loaded; the session stays `loading` meanwhile (AUTH-166). */
  isLoaded: boolean;
  /** The Clerk user id, or null when Clerk has no session. A change reloads `me`. */
  userId: string | null;
  /** A fresh session token, or null without a session. */
  getToken: () => Promise<string | null>;
  /** Ends the Clerk session (AUTH-167). */
  signOut: () => Promise<void>;
}

const SIGN_OUT_UNREACHABLE = 'Sign-out failed: the server could not be reached. Check your connection and try again.';

function isSessionUser(value: unknown): value is SessionUser {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.name === 'string' && Array.isArray(v.providers);
}

/** Reads ROUTES.me. 401 means signed out; anything unexpected is reported, not guessed. */
export async function loadSession(fetcher: FetchLike): Promise<SessionState> {
  let response: Response;
  try {
    response = await fetcher(ROUTES.me, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  } catch (error) {
    return { status: 'error', message: error instanceof Error ? error.message : String(error) };
  }
  if (response.status === 401) return { status: 'signed_out' };
  if (!response.ok) return { status: 'error', message: `The server responded with ${response.status}.` };
  try {
    const body = (await response.json()) as Partial<MeResponse>;
    if (isSessionUser(body.user)) return { status: 'signed_in', user: body.user };
  } catch {
    // Falls through: a non-JSON body (for example the app's own index.html) is not a session.
  }
  return { status: 'error', message: 'The server returned something that is not a session.' };
}

interface SessionProviderProps {
  children: ReactNode;
  /** Injectable for tests. Defaults to window.fetch (with the Clerk token when Clerk is active). */
  fetcher?: FetchLike;
  /** Present while Clerk is active (AUTH-166). */
  clerk?: ClerkAdapter;
}

/** Loads the current session once and shares it. Must sit inside the router, because sign-out navigates. */
export function SessionProvider({ children, fetcher = defaultFetch, clerk }: SessionProviderProps) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [generation, setGeneration] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [leaveGuard] = useState<LeaveGuardRegistry>(() => ({ current: null }));
  // Set once the server has ended the session; the state changes only when the landing page is showing.
  const signedOutPending = useRef(false);
  // The latest adapter, so the fetch below keeps one identity while Clerk re-renders.
  const clerkRef = useRef(clerk);
  clerkRef.current = clerk;
  const hasClerk = clerk !== undefined;
  const clerkLoaded = clerk?.isLoaded ?? true;
  const clerkUserId = clerk?.userId ?? null;

  // Every API request carries a fresh Clerk token while Clerk is active (AUTH-166).
  const apiFetch = useMemo<FetchLike>(
    () => (hasClerk ? withBearerToken(fetcher, () => clerkRef.current?.getToken() ?? Promise.resolve(null)) : fetcher),
    [fetcher, hasClerk],
  );

  useEffect(() => {
    // Until Clerk has loaded there is no token yet; asking now would report a signed-in reader as signed out.
    // While signing out, Clerk's user disappears first; the landing-page effect below settles the state.
    if (!clerkLoaded || signedOutPending.current) return;
    let live = true;
    loadSession(apiFetch).then((next) => {
      if (live) setState(next);
    });
    return () => {
      live = false;
    };
  }, [apiFetch, generation, clerkLoaded, clerkUserId]);

  const refresh = useCallback(() => setGeneration((g) => g + 1), []);

  const signOut = useCallback(async () => {
    // Ask before anything is ended: once the session is gone, RequireAuth unmounts the page and its guard.
    const guard = leaveGuard.current;
    if (guard && !(await guard.confirmLeave())) return false;
    const response = await apiFetch(ROUTES.logout, { method: 'POST', credentials: 'same-origin' });
    // 401 means there was no session to end, which is the outcome the reader wanted.
    if (!response.ok && response.status !== 401) {
      throw new Error(`Sign-out failed: the server responded with ${response.status}.`);
    }
    // The reader already agreed to leave; do not ask again on the way out.
    leaveGuard.current?.allowNextNavigation();
    signedOutPending.current = true;
    const adapter = clerkRef.current;
    if (adapter) {
      try {
        // Ends the Clerk session too (AUTH-167); until it does, the reader is still signed in.
        await adapter.signOut();
      } catch {
        signedOutPending.current = false;
        throw new Error(SIGN_OUT_UNREACHABLE);
      }
    }
    // Router updates are transitions, so marking the session signed out now could render the old, protected
    // page first, and RequireAuth would send the reader to /login. The effect below waits for the landing page.
    await navigate('/');
    return true;
  }, [apiFetch, navigate, leaveGuard]);

  useEffect(() => {
    if (signedOutPending.current && location.pathname === '/') {
      signedOutPending.current = false;
      setState({ status: 'signed_out' });
    }
  }, [location]);

  const value = useMemo<SessionContextValue>(
    () => ({ state, user: state.status === 'signed_in' ? state.user : null, refresh, signOut, apiFetch }),
    [state, refresh, signOut, apiFetch],
  );

  return (
    <SessionContext value={value}>
      <LeaveGuardContext value={leaveGuard}>{children}</LeaveGuardContext>
    </SessionContext>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/** Same-origin path that is safe to return to after sign-in, or null. Rejects other origins and protocol-relative URLs. */
/**
 * A same-origin relative path, or null. Mirrors the Worker's check (apps/api/src/auth/oauth.ts):
 * the raw value and its normalised form must both be plain paths, because dot segments such as
 * '/.//evil.example' normalise to the protocol-relative '//evil.example'.
 */
export function safeReturnTo(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  if (/[\\\u0000-\u001f\u007f\s]/.test(value)) return null;
  const origin = 'https://return-to.invalid';
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || url.pathname.startsWith('//') || url.pathname.includes('\\')) return null;
  if (url.pathname === '/auth' || url.pathname.startsWith('/auth/')) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function loginPath(returnTo: string): string {
  return `/login?${new URLSearchParams({ [RETURN_TO_PARAM]: returnTo }).toString()}`;
}

/**
 * Route guard. Signed-out readers go to /login with the page they asked for,
 * so sign-in can bring them back. Wraps child routes (as an Outlet) or children.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { state, refresh } = useSession();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="page-wrap">
        <p className="loading" role="status">
          Checking your sign-in
        </p>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="page-wrap">
        <div className="empty" role="alert">
          <h1>Your sign-in could not be checked</h1>
          <p>{state.message} This page needs you to be signed in.</p>
          <p>
            <button type="button" className="button" onClick={refresh}>
              Try again
            </button>
          </p>
        </div>
      </div>
    );
  }
  if (state.status === 'signed_out') {
    return <Navigate to={loginPath(location.pathname + location.search)} replace />;
  }
  return children ?? <Outlet />;
}
